/**
 * usePresenceManager Hook
 * Custom hook for PresenceManager
 * Wraps the Zustand store with convenient access methods
 * Follows the useEnrichedAuth pattern
 */

import { useMemo } from 'react';
import { usePresenceManagerStore } from '@/stores/presenceManagerStore';

/**
 * Custom hook for PresenceManager
 * Provides convenient access to presence state and actions
 */
export function usePresenceManager() {
  const store = usePresenceManagerStore();

  // Helper: Get online users as array (sorted by name) - MEMOIZED
  const onlineUsersArray = useMemo(() => {
    return Array.from(store.onlineUsers.values()).sort((a, b) => {
      const nameA = a.userName || a.userId;
      const nameB = b.userName || b.userId;
      return nameA.localeCompare(nameB);
    });
  }, [store.onlineUsers]);

  // Helper: Get user by ID
  const getUserPresence = (userId: string) => {
    return store.onlineUsers.get(userId) || null;
  };

  // Helper: Get formatted idle time
  const getFormattedIdleTime = () => {
    const seconds = store.idleCountdown;
    if (seconds <= 0) return 'Idle now';

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return {
    // State
    manager: store.manager,
    isConnected: store.isConnected,
    connectionState: store.connectionState,
    currentUserPresence: store.currentUserPresence,
    onlineUsers: onlineUsersArray,
    onlineUsersMap: store.onlineUsers,
    isIdle: store.isIdle,
    lastHeartbeat: store.lastHeartbeat,
    lastActivity: store.lastActivity,
    idleCountdown: store.idleCountdown,
    formattedIdleTime: getFormattedIdleTime(),
    presenceStats: store.presenceStats,
    isAdmin: store.isAdmin,
    config: store.config,

    // Actions
    initialize: store.initialize,
    connect: store.connect,
    disconnect: store.disconnect,
    updateStatus: store.updateStatus,
    refreshOnlineUsers: store.refreshOnlineUsers,
    refreshStats: store.refreshStats,
    updateConfig: store.updateConfig,
    destroy: store.destroy,

    // Helpers
    getUserPresence,
  };
}
